import React, { Fragment, createRef } from 'react'
import { Button, Checkbox, Loader, AutocompleteSearch, SortableTable, TextOverflow } from 'components/UIElements'
import PropTypes from 'prop-types'
import '../../../ParticipantsPage/stylesheets/ParticipantTable.scss'
import { MODAL_CONTENT_MAP, MODAL_BUTTONS_MAP, MODAL_CLASSES_MAP, STUDY_CONFIG_FEATURE_KEY_MAP, PARTICIPANT_TABLE_ROW_KEY_MAP } from 'utils/constants'
import { filterObjectByKey } from 'utils/object'
import FirstLoginFilter from '../../../ParticipantsPage/components/Filters/FirstLoginFilter'
import getStringFilter from '../../../ParticipantsPage/components/Filters/StringFilter'
import CaregiverView from '../../CreateCaregiver/containers/CreateCaregiverContainer'
import { getInLeafSiteFromRow, getValueFromRowByKey } from '../../../../utils/pariticipantUtils'

export const KEY_NAME_MAP = {
  checkbox: null,
  relationToParticipant: 'RELATION TO PARTICIPANT',
  id: 'ID',
  subjectId: 'Subject ID',
  lastName: 'Last Name',
  firstName: 'Preferred Name',
  siteID: 'Site ID',
  siteName: 'Site Name',
  firstLogin: 'First Login',
}

export const KEY_TOOLTIP_MAP = {
  lastPassiveData: 'Last time the caregiver device successfully sent passive data to the system',
  lastConnection: 'Last time the caregiver device successfully retrieved pending instruments',
}

export const NO_CONSENT_MAP = {
  checkbox: null,
  relationToParticipant: 'RELATION TO PARTICIPANT',
  id: 'ID',
  subjectId: 'Subject ID',
  lastName: 'Last Name',
  firstName: 'Preferred name',
  siteID: 'Site ID',
  siteName: 'Site Name',
  firstLogin: 'First Login',
}

export const CAREGIVER_STATUS_TYPE_MAP = {
  accountRecovery: 'account-recovery',
  completeAcccountSetup: 'complete-account-setup',
  pending: 'pending',
}

const RESEND_INVITE = 'Resend Invite'

// Filter function for NO_CONSENT_MAP entries based on showCheckbox and showRelationToParticipant flags
const getNoConsentMapFilter = (showCheckbox, showRelationToParticipant) => ([key]) =>
  (showCheckbox || key !== PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX) &&
  (showRelationToParticipant || key !== PARTICIPANT_TABLE_ROW_KEY_MAP.RELATION_TO_PARTICIPANT)

class CaregiversTable extends React.Component {
  constructor(props) {
    super(props)
    const { consentRequired, hasPiiRole, caregiverDataFields, showRelationToParticipant, showCheckbox } = this.props
    const hasParticipantDataDisabled = caregiverDataFields?.disabled
    this.ptpTableRef = createRef(null)
    this.disabledDataFields =
      hasParticipantDataDisabled &&
      hasParticipantDataDisabled.map(data => {
        switch (data.metadata.internal_key) {
          case 'fname':
            return 'firstName'
          case 'lname':
            return 'lastName'
          default:
            return data.metadata.internal_key
        }
      })
    const filterKey = key =>
      (hasPiiRole || (key !== 'lastName' && key !== 'firstName')) &&
      (showRelationToParticipant || key !== PARTICIPANT_TABLE_ROW_KEY_MAP.RELATION_TO_PARTICIPANT) &&
      (showCheckbox || key !== PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX)

    const newNameMap = filterObjectByKey(KEY_NAME_MAP, filterKey)
    const filteredMap = consentRequired
      ? newNameMap
      : Object.fromEntries(
        Object.entries(NO_CONSENT_MAP).filter(getNoConsentMapFilter(showCheckbox, showRelationToParticipant)),
      )
    this.headerOptions = Object.keys(filteredMap).map(key => {
      const className = KEY_NAME_MAP[key]
        ? KEY_NAME_MAP[key]
          .toLowerCase()
          .split(' ')
          .join('-')
        : undefined

      const iconClassName = KEY_TOOLTIP_MAP[key] ? 'fas fa-info-circle' : null
      const popupText = KEY_TOOLTIP_MAP[key] || null

      return {
        key,
        sortable: true,
        component: newNameMap[key],
        className,
        iconClassName,
        popupText,
      }
    })
    if (hasParticipantDataDisabled) {
      this.headerOptions = this.headerOptions.filter(option => !this.disabledDataFields.includes(option.key))
    }
    this.listOfRelations = [
      { key: 'Parent / Guardian', text: 'Parent / Guardian' },
      { key: 'Family Member', text: 'Family Member' },
      { key: 'Professional Caregiver', text: 'Professional Caregiver' },
      { key: 'Other', text: 'Other' },
    ]

    if (showCheckbox) this.headerOptions[0].sortable = false
    if (showRelationToParticipant) this.headerOptions[1].sortable = false

    this.filterComponents = {
      id: getStringFilter({ header: 'ID', dataKey: PARTICIPANT_TABLE_ROW_KEY_MAP.ID }),
      subjectId: getStringFilter({ header: 'Subject ID', dataKey: 'subjectId' }),
      lastName: getStringFilter({ header: 'Last name', dataKey: 'lastName' }),
      firstName: getStringFilter({ header: 'Preferred name', dataKey: 'firstName' }),
      firstLogin: FirstLoginFilter,
    }
    this.renderFunctions = {
      checkbox: showCheckbox ? this.renderCheckboxCell : null,
      relationToParticipant: showRelationToParticipant ? this.renderDropdownCell : null,
      id: this.renderIdCell,
      lastName: this.renderNameCell,
      firstName: this.renderNameCell,
      firstLogin: this.renderFirstLoginCell,
    }
    this.initialize()
  }

  onOpenPtpPreview(link, ptpSiteID, id, resetParticipant, closeDrawer) {
    const { openDrawer, resetDocs } = this.props
    openDrawer({
      closeOnBackgroundClick: true,
      onCancel: () => {
        resetParticipant()
        closeDrawer()
        resetDocs()
      },
      content: <CaregiverView {...this.props} ptpId={id} link={link} inDrawer ptpSiteID={ptpSiteID} />,
    })
  }

  onResendInvite = ({ username, ptpId }) => {
    const { openModal, closeModal, triggerResendInvite } = this.props
    openModal({
      content: MODAL_CONTENT_MAP.newInvitation,
      confirmButton: MODAL_BUTTONS_MAP.confirm,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      className: MODAL_CLASSES_MAP.confirmation,
      onConfirm: () => {
        triggerResendInvite({ username, ptpId })
        closeModal()
      },
    })
  }

  initialize() {
    const { filterProps } = this.props
    if (filterProps) {
      this.headerOptions.forEach(option => {
        if (this.filterComponents.hasOwnProperty(option.key)) {
          const Component = this.filterComponents[option.key]
          option.component = (
            <span>
              {option.component}
              <Component />
            </span>
          )
        }
      })
    }
  }

  filterList(list) {
    const { consentRequired, noTracksFeature } = this.props
    const newList =
      this.disabledDataFields?.length > 0
        ? list
          .filter(item => !!item && Array.isArray(item))
          .map(caregiver => caregiver.filter(el => !this.disabledDataFields.includes(el.key)))
        : list
    if (!consentRequired) {
      for (let i = 0; i < newList.length; i += 1) {
        newList[i] = newList[i]?.filter(col => {
          return col.key !== STUDY_CONFIG_FEATURE_KEY_MAP.consent
        })
      }
    }
    if (noTracksFeature) {
      for (let i = 0; i < newList.length; i += 1) {
        newList[i] = newList[i]?.filter(col => {
          return col.key !== STUDY_CONFIG_FEATURE_KEY_MAP.tracks
        })
      }
    }
    return newList
  }

  renderNameCell = ({ value, idx, row }) => {
    const tableList = this.ptpTableRef.current ? this.ptpTableRef.current.getList() : null
    const lastTableItem = tableList ? tableList[tableList.length - 1] : null
    return (
      <td key={`row_${idx}`} className='compliance-row'>
        <TextOverflow
          width={160}
          text={value}
          position={lastTableItem && lastTableItem[1].value === row[1].value ? 'top' : 'bottom'}
        />
      </td>
    )
  }

  renderFirstLoginCell = ({ value, idx, username, ptpId }) => {
    const { canResendInviteOrResetPw, isEmailless, studyLock } = this.props
    let content = <span>{value}</span>
    if (value.includes(RESEND_INVITE)) {
      const [firstSection, secondSection] = value.split(RESEND_INVITE)
      content = (
        <>
          <p>{firstSection}</p>
          {canResendInviteOrResetPw && !isEmailless && (
            <Button
              link
              disabled={studyLock}
              content={RESEND_INVITE}
              id={`resend-invite-${username}`}
              onClick={e => {
                e.stopPropagation()
                this.onResendInvite({ username, ptpId })
              }}
            />
          )}
          <p>{secondSection}</p>
        </>
      )
    }
    return (
      <td key={`col_${idx}`} className='first-login-row'>
        {content}
      </td>
    )
  }

  renderIdCell = ({ value, idx, isPtpDisabled }) => {
    const accountDisabledText =
      "This participant's account is disabled. Data collection is paused for the participant, and the participant will not be able to login until the account is enabled."
    return (
      <td key={`id-${idx}`} className='id-row'>
        {isPtpDisabled ? (
          <div className='flexed'>
            <Fragment key={idx}>
              <i className='fas fa-lock' />
              <div className='account-disabled-tooltip'>{accountDisabledText}</div>
            </Fragment>
            <span>{value}</span>
          </div>
        ) : (
          <div>{value}</div>
        )}
      </td>
    )
  }

  renderCheckboxCell = ({ value, idx, compliance, siteID }) => {
    const { toggleCaregiver, dataSectionSelected, checkedCaregivers } = this.props
    return (
      <td
        className={`checkbox-cell${dataSectionSelected ? ` ${dataSectionSelected}` : compliance ? ` ${compliance.split(' ').pop()}` : ''
          }`}
        onClick={e => {
          e.stopPropagation()
          toggleCaregiver(value, siteID)
        }}
        key={`col_${idx}`}>
        <Checkbox className='bubble' checked={Boolean(checkedCaregivers?.[value])} />
      </td>
    )
  }

  renderDropdownCell = ({ value, idx, compliance, siteID, row }) => {
    const { dataSectionSelected, updateCaregiverRelation } = this.props

    const caregiverId = row.find(col => col.key === PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX)?.value
    const selectedRelation = row.find(cell => cell.key === PARTICIPANT_TABLE_ROW_KEY_MAP.RELATION_TO_PARTICIPANT)?.value || ''
    const fallbackRelation = this.listOfRelations[this.listOfRelations.length - 1]?.text || ''

    const setSelectedItem = item => {
      updateCaregiverRelation?.(caregiverId, item)
    }

    return (
      <td
        className={`checkbox-cell${dataSectionSelected ? ` ${dataSectionSelected}` : compliance ? ` ${compliance.split(' ').pop()}` : ''
          }`}
        key={`col_${idx}`}>
        <AutocompleteSearch
          id='select_relation'
          checked={{ text: selectedRelation }}
          shouldReturnItem
          toggleItem={setSelectedItem}
          placeholder='Select relation'
          isDropdown
          hideCheckbox
          initialValue={value || fallbackRelation}
          list={this.listOfRelations}
        />
      </td>
    )
  }

  render() {
    const {
      closeDrawer,
      disableRowClick,
      filterProps,
      hasOrphans,
      offsets,
      caregiversList,
      resetParticipant,
      studyID,
      hasPiiRole,
      canMovePtp,
      studyLock,
      hasOverFlow,
      studyVersionID,
      showCheckbox,
      toggleCheckAll,
      leafSites,
      showRelationToParticipant,
    } = this.props
    const canNotMoveAndLocked = !canMovePtp && studyLock
    const rowList = this.filterList(
      caregiversList.map(caregiver =>
        caregiver.filter(column => {
          const keysToRemove = []
          if (canNotMoveAndLocked) keysToRemove.push(PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX)
          return !keysToRemove.includes(column.key)
        }),
      ),
    )
    if (showCheckbox) {
      this.headerOptions[0].func = true
      this.headerOptions[0].component = self => (
        <Checkbox
          className='buble'
          checked={Object.keys(caregiversList).length === caregiversList.length}
          onClick={() => {
            const filterdParticipants = self.currentList.reduce((acc, cur) => {
              acc[cur[0].value] = cur
              return acc
            }, {})
            toggleCheckAll(filterdParticipants)
          }}
        />
      )
    }

    const rowHighlightOptions = [
      {
        hasHighlightClassName: row =>
          row.find(item => item.key === 'accountDeletionStatus')?.value === CAREGIVER_STATUS_TYPE_MAP.pending,
        highlightClassName: 'highlighted account-deletion',
        highlightSecondaryClassName: 'border-top',
      },
      {
        hasHighlightClassName: row =>
          row.find(item => item.key === PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX)?.compliance === CAREGIVER_STATUS_TYPE_MAP.completeAcccountSetup,
        highlightClassName: 'highlighted complete-account-setup',
      },
      {
        hasHighlightClassName: row =>
          row.find(item => item.key === PARTICIPANT_TABLE_ROW_KEY_MAP.CHECKBOX)?.compliance.includes(CAREGIVER_STATUS_TYPE_MAP.accountRecovery),
        highlightClassName: 'highlighted account-recovery',
      },
    ]

    return (
      <SortableTable
        ref={this.ptpTableRef}
        id='participant-table'
        offset={offsets ? offsets[1] : 0}
        filters={filterProps ? filterProps.filters : undefined}
        onRowClick={
          disableRowClick
            ? null
            : rowData => {
              const cgID = getValueFromRowByKey(rowData, PARTICIPANT_TABLE_ROW_KEY_MAP.ID)
              if (!cgID) return

              let sideId = null

              for (const row of caregiversList) {
                const rowCgID = getValueFromRowByKey(row, PARTICIPANT_TABLE_ROW_KEY_MAP.ID)
                const rowSiteId = getValueFromRowByKey(row, PARTICIPANT_TABLE_ROW_KEY_MAP.SITE_ID)

                if (rowCgID === cgID) {
                  sideId = rowSiteId
                  break
                }
              }
              const editLink = `/studies/${studyID}/${studyVersionID}/participants/caregiver/${cgID}/caregiversSite/${sideId}`
              this.onOpenPtpPreview(editLink, sideId, cgID, resetParticipant, closeDrawer)
            }
        }
        rowClassNameOptions={{
          hasClassName: row => {
            const { leafSites } = this.props
            const hasLeafSites = leafSites && leafSites.length > 0
            // Only apply orphan styling if there are leaf sites in the current study version
            return !getInLeafSiteFromRow(row) && hasOrphans && hasLeafSites
          },
          className: 'is-non-leaf',
        }}
        rowHighlightOptions={rowHighlightOptions}
        className={`participant-list ${hasOverFlow ? 'overflow' : ''}`}
        sortingBy={hasPiiRole ? 'lastName' : 'name'}
        rowList={rowList}
        headerOptions={this.headerOptions}
        emptyText={
          showRelationToParticipant
            ? "You haven't assigned any caregivers yet. To assign a caregiver, use the search bar to find and link them to the participant."
            : 'No Caregivers'
        }
        renderFunctions={this.renderFunctions}
        viewMoreWithTablePages={{ minRows: 20, maxRows: 20 }}
      />
    )
  }
}

CaregiversTable.propTypes = {
  canMovePtp: PropTypes.bool,
  canResendInviteOrResetPw: PropTypes.bool,
  checkedCaregivers: PropTypes.object,
  closeDrawer: PropTypes.func,
  closeModal: PropTypes.func,
  compliance: PropTypes.objectOf(PropTypes.number),
  consentRequired: PropTypes.bool,
  disableRowClick: PropTypes.bool,
  filterProps: PropTypes.object,
  hasOrphans: PropTypes.bool,
  hasOverFlow: PropTypes.bool,
  hasPiiRole: PropTypes.bool,
  isEmailless: PropTypes.bool,
  noTracksFeature: PropTypes.bool,
  offsets: PropTypes.arrayOf(PropTypes.number),
  openDrawer: PropTypes.func,
  openModal: PropTypes.func,
  participant: PropTypes.object,
  caregiverDataFields: PropTypes.objectOf(PropTypes.array),
  caregiversList: PropTypes.arrayOf(PropTypes.array),
  resetDocs: PropTypes.func,
  resetParticipant: PropTypes.func,
  searchTerm: PropTypes.string,
  siteID: PropTypes.number,
  studyID: PropTypes.number,
  studyLock: PropTypes.bool,
  toggleCheckAll: PropTypes.func,
  toggleCaregiver: PropTypes.func,
  toggleUncheckAll: PropTypes.func,
  triggerResendInvite: PropTypes.func,
  showCheckbox: PropTypes.bool,
  showRelationToParticipant: PropTypes.bool,
}

export default CaregiversTable
